import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
export interface NumberBoardProps {
  /** Starting number (default 1) */
  start?: number;
  /** Ending number (default 100) */
  end?: number;
  /** Numbers to highlight */
  highlight?: number[];
  /** Numbers to mark with a different color */
  marked?: number[];
  /** Number of columns (default 10) */
  columns?: number;
  /** Label text */
  label?: string;
  /** Display mode */
  mode?: 'stimulus' | 'support';
  /** Whether to show a pattern hint */
  showPattern?: boolean;
}
export function NumberBoard({
  start = 1,
  end = 100,
  highlight = [],
  marked = [],
  columns = 10,
  label,
  mode = 'support',
  showPattern = false
}: NumberBoardProps) {
  const numbers = useMemo(() => {
    const nums: number[] = [];
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  }, [start, end]);
  const highlightSet = useMemo(() => new Set(highlight), [highlight]);
  const markedSet = useMemo(() => new Set(marked), [marked]);
  const rows = Math.ceil(numbers.length / columns);
  // Detect pattern for support mode
  const patternDescription = useMemo(() => {
    if (!showPattern || highlight.length < 2) return null;
    const sorted = [...highlight].sort((a, b) => a - b);
    const diffs = sorted.slice(1).map((n, i) => n - sorted[i]);
    const allSame = diffs.every(d => d === diffs[0]);
    if (allSame && diffs.length > 0) {
      return `De gemarkeerde getallen hebben steeds een verschil van ${diffs[0]}.`;
    }
    // Check if all multiples of same number
    const divisors = [2, 3, 4, 5, 6, 7, 8, 9, 10];
    for (const d of divisors) {
      if (sorted.every(n => n % d === 0)) {
        return `Dit zijn de veelvouden van ${d}.`;
      }
    }
    return null;
  }, [highlight, showPattern]);
  return <div className={`${mode === 'support' ? 'bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-2xl border-2 border-amber-200 shadow-sm' : 'bg-amber-50 rounded-xl px-4 pt-3 pb-5 border border-amber-200'}`} data-id="element-9498">
      {label && <p className={`text-sm font-semibold text-amber-800 mb-3 text-center ${mode === 'support' ? 'text-base' : ''}`} data-id="element-9499">
          {label}
        </p>}

      <div className="grid gap-0.5 mx-auto" style={{
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      maxWidth: `${columns * 38}px`
    }} role="grid" aria-label={`Getallenbord van ${start} tot ${end}`} data-id="element-9500">
        {numbers.map((num, idx) => {
        const isHighlighted = highlightSet.has(num);
        const isMarked = markedSet.has(num);
        return <motion.div key={num} initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: idx * 0.004,
          type: 'spring',
          stiffness: 500,
          damping: 30
        }} className={`
                flex items-center justify-center text-xs font-semibold rounded-sm
                aspect-square select-none
                ${isHighlighted ? 'bg-indigo-600 text-white ring-2 ring-indigo-300 shadow-md z-10' : isMarked ? 'bg-amber-400 text-amber-900 ring-1 ring-amber-500' : 'bg-white text-slate-700 border border-slate-200'}
              `} role="gridcell" aria-label={`${num}${isHighlighted ? ' (gemarkeerd)' : ''}`} data-id="element-9501">
              {mode === 'stimulus' && isHighlighted ? '?' : num}
            </motion.div>;
      })}
      </div>

      {/* Pattern explanation in support mode */}
      {mode === 'support' && patternDescription && <div className="mt-4 bg-white/80 rounded-xl p-4 border border-amber-100" data-id="element-9502">
          <div className="flex items-start gap-2" data-id="element-9503">
            <span className="text-lg" data-id="element-9504">💡</span>
            <p className="text-sm text-slate-700" data-id="element-9505">{patternDescription}</p>
          </div>
        </div>}

      {/* Legend */}
      {mode === 'support' && (highlight.length > 0 || marked.length > 0) && <div className="mt-3 flex items-center justify-center gap-4 text-xs text-slate-600" data-id="element-9506">
          {highlight.length > 0 && <div className="flex items-center gap-1.5" data-id="element-9507">
              <div className="w-4 h-4 rounded-sm bg-indigo-600" data-id="element-9508" />
              <span data-id="element-9509">Gemarkeerd</span>
            </div>}
          {marked.length > 0 && <div className="flex items-center gap-1.5" data-id="element-9510">
              <div className="w-4 h-4 rounded-sm bg-amber-400 border border-amber-500" data-id="element-9511" />
              <span data-id="element-9512">Extra</span>
            </div>}
        </div>}
    </div>;
}